import { ApiError } from '@/shared/errors/api-error';
import { isRequestCanceled } from '@/shared/errors/is-request-canceled';
import type { ErrorReportingDisposition } from '@/shared/observability/error-reporting';

export type AppErrorCategory = string;
export type AppErrorSeverity = 'info' | 'warn' | 'error' | 'fatal';
export type AppErrorCapability = string;
export type AppErrorLayer = string;
export type AppErrorOperation = string;
export type AppErrorBoundaryDisposition = ErrorReportingDisposition;

export type AppErrorDescriptor = {
    code: string;
    summary: string;
    category: AppErrorCategory;
    severity?: AppErrorSeverity;
    hint?: string | null;
    retryable?: boolean | null;
    requestId?: string | null;
    correlationId?: string | null;
    operationId?: string | null;
    capability?: AppErrorCapability | null;
    layer?: AppErrorLayer | null;
    operation?: AppErrorOperation | null;
    boundaryDisposition?: AppErrorBoundaryDisposition | null;
    clientId?: string | null;
    platform?: string | null;
    route?: string | null;
    jobId?: string | null;
    fingerprint?: string | null;
    recommendedAction?: string | null;
    status?: number | null;
    details?: unknown;
    originalName?: string | null;
    cause?: unknown;
};

export type NormalizeUnknownErrorOptions = Omit<AppErrorDescriptor, 'code' | 'summary' | 'category'> & {
    defaultCode?: string;
    defaultSummary?: string;
    defaultCategory?: AppErrorCategory;
};

const DEFAULT_APP_ERROR_CODE = 'UnexpectedRuntimeError';
const DEFAULT_APP_ERROR_SUMMARY = 'An unexpected runtime error occurred.';
const DEFAULT_APP_ERROR_CATEGORY = 'internal';

const isObjectRecord = (value: unknown): value is Record<string, unknown> => {
    return Object.prototype.toString.call(value) === '[object Object]';
};

const readOptionalString = (value: unknown): string | null => {
    if (typeof value !== 'string') {
        return null;
    }

    const normalized = value.trim();
    return normalized ? normalized : null;
};

const readOptionalBoolean = (value: unknown): boolean | null => {
    return typeof value === 'boolean' ? value : null;
};

const readOptionalNumber = (value: unknown): number | null => {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const readOptionalSeverity = (value: unknown): AppErrorSeverity | null => {
    switch (value) {
        case 'info':
        case 'warn':
        case 'error':
        case 'fatal':
            return value;
        default:
            return null;
    }
};

const readOptionalBoundaryDisposition = (value: unknown): AppErrorBoundaryDisposition | null => {
    switch (value) {
        case 'terminal':
        case 'rethrow':
            return value;
        default:
            return null;
    }
};

const resolveFallbackCategory = (error: unknown, defaultCategory?: AppErrorCategory): AppErrorCategory => {
    if (isRequestCanceled(error)) {
        return 'canceled';
    }

    return defaultCategory ?? DEFAULT_APP_ERROR_CATEGORY;
};

const resolveFallbackSummary = (value: unknown, defaultSummary?: string): string => {
    return readOptionalString(value) ?? defaultSummary ?? DEFAULT_APP_ERROR_SUMMARY;
};

const resolveFallbackOriginalName = (error: unknown): string | null => {
    if (error instanceof Error) {
        return readOptionalString(error.name) ?? 'Error';
    }

    if (isObjectRecord(error)) {
        return readOptionalString(error.name) ?? 'Error';
    }

    if (error === null || error === undefined) {
        return null;
    }

    return typeof error;
};

const isAppErrorRecord = (error: unknown): error is AppError => {
    return error instanceof AppError;
};

const readDescriptorValue = <T>(primary: T | null | undefined, fallback: T | null | undefined): T | undefined => {
    if (primary !== null && primary !== undefined) {
        return primary;
    }

    if (fallback !== null && fallback !== undefined) {
        return fallback;
    }

    return undefined;
};

const toCause = (value: unknown): Error | undefined => {
    return value instanceof Error ? value : undefined;
};

export class AppError extends Error {
    readonly code: string;
    readonly summary: string;
    readonly category: AppErrorCategory;
    readonly severity: AppErrorSeverity;
    readonly hint: string | null;
    readonly retryable: boolean | null;
    readonly requestId: string | null;
    readonly correlationId: string | null;
    readonly operationId: string | null;
    readonly capability: AppErrorCapability | null;
    readonly layer: AppErrorLayer | null;
    readonly operation: AppErrorOperation | null;
    readonly boundaryDisposition: AppErrorBoundaryDisposition | null;
    readonly clientId: string | null;
    readonly platform: string | null;
    readonly route: string | null;
    readonly jobId: string | null;
    readonly fingerprint: string | null;
    readonly recommendedAction: string | null;
    readonly status: number | null;
    readonly details: unknown;
    readonly originalName: string | null;

    constructor(descriptor: AppErrorDescriptor) {
        super(descriptor.summary, {
            cause: toCause(descriptor.cause),
        });

        this.name = 'AppError';
        this.code = descriptor.code;
        this.summary = descriptor.summary;
        this.category = descriptor.category;
        this.severity = descriptor.severity ?? 'error';
        this.hint = descriptor.hint ?? null;
        this.retryable = descriptor.retryable ?? null;
        this.requestId = descriptor.requestId ?? null;
        this.correlationId = descriptor.correlationId ?? descriptor.requestId ?? null;
        this.operationId = descriptor.operationId ?? null;
        this.capability = descriptor.capability ?? null;
        this.layer = descriptor.layer ?? null;
        this.operation = descriptor.operation ?? null;
        this.boundaryDisposition = descriptor.boundaryDisposition ?? null;
        this.clientId = descriptor.clientId ?? null;
        this.platform = descriptor.platform ?? null;
        this.route = descriptor.route ?? null;
        this.jobId = descriptor.jobId ?? null;
        this.fingerprint = descriptor.fingerprint ?? null;
        this.recommendedAction = descriptor.recommendedAction ?? null;
        this.status = descriptor.status ?? null;
        this.details = descriptor.details;
        this.originalName = descriptor.originalName ?? resolveFallbackOriginalName(descriptor.cause) ?? 'Error';

        if (descriptor.cause instanceof Error && descriptor.cause.stack) {
            this.stack = descriptor.cause.stack;
        }
    }
}

export const isAppError = (error: unknown): error is AppError => {
    return isAppErrorRecord(error);
};

export const readAppErrorDetailsRecord = (error: unknown): Record<string, unknown> | undefined => {
    if (!(error instanceof AppError) || !isObjectRecord(error.details)) {
        return undefined;
    }

    return error.details;
};

export const createAppError = (descriptor: AppErrorDescriptor): AppError => {
    return new AppError(descriptor);
};

const toAppErrorFromApiError = (error: ApiError, options: NormalizeUnknownErrorOptions = {}): AppError => {
    return createAppError({
        code: error.code,
        summary: resolveFallbackSummary(error.message, options.defaultSummary),
        category: error.category ?? resolveFallbackCategory(error, options.defaultCategory),
        severity: options.severity ?? (error.status >= 500 ? 'error' : 'warn'),
        hint: readDescriptorValue(error.hint, options.hint),
        retryable: readDescriptorValue(error.retryable, options.retryable),
        requestId: readDescriptorValue(error.requestId, options.requestId),
        correlationId: readDescriptorValue(error.correlationId, options.correlationId ?? error.requestId),
        operationId: readDescriptorValue(error.operationId, options.operationId),
        capability: options.capability,
        layer: options.layer,
        operation: options.operation,
        boundaryDisposition: options.boundaryDisposition,
        clientId: options.clientId,
        platform: options.platform,
        route: options.route,
        jobId: options.jobId,
        fingerprint: options.fingerprint,
        recommendedAction: options.recommendedAction,
        status: readDescriptorValue(error.status, options.status),
        details: readDescriptorValue(error.details, options.details),
        originalName: readDescriptorValue(error.name, options.originalName),
        cause: error,
    });
};

const toAppErrorFromError = (error: Error, options: NormalizeUnknownErrorOptions = {}): AppError => {
    const errorRecord = error as Error & Record<string, unknown>;

    return createAppError({
        code: readOptionalString(errorRecord.code) ?? options.defaultCode ?? DEFAULT_APP_ERROR_CODE,
        summary: resolveFallbackSummary(error.message, options.defaultSummary),
        category: readOptionalString(errorRecord.category) ?? resolveFallbackCategory(error, options.defaultCategory),
        severity: readOptionalSeverity(errorRecord.severity) ?? options.severity ?? 'error',
        hint: readDescriptorValue(readOptionalString(errorRecord.hint), options.hint),
        retryable: readDescriptorValue(readOptionalBoolean(errorRecord.retryable), options.retryable),
        requestId: readDescriptorValue(readOptionalString(errorRecord.requestId), options.requestId),
        correlationId: readDescriptorValue(readOptionalString(errorRecord.correlationId), options.correlationId),
        operationId: readDescriptorValue(readOptionalString(errorRecord.operationId), options.operationId),
        capability: readDescriptorValue(readOptionalString(errorRecord.capability), options.capability),
        layer: readDescriptorValue(readOptionalString(errorRecord.layer), options.layer),
        operation: readDescriptorValue(readOptionalString(errorRecord.operation), options.operation),
        boundaryDisposition: readDescriptorValue(
            readOptionalBoundaryDisposition(errorRecord.boundaryDisposition),
            options.boundaryDisposition,
        ),
        clientId: readDescriptorValue(readOptionalString(errorRecord.clientId), options.clientId),
        platform: readDescriptorValue(readOptionalString(errorRecord.platform), options.platform),
        route: readDescriptorValue(readOptionalString(errorRecord.route), options.route),
        jobId: readDescriptorValue(readOptionalString(errorRecord.jobId), options.jobId),
        fingerprint: readDescriptorValue(readOptionalString(errorRecord.fingerprint), options.fingerprint),
        recommendedAction: readDescriptorValue(
            readOptionalString(errorRecord.recommendedAction),
            options.recommendedAction,
        ),
        status: readDescriptorValue(readOptionalNumber(errorRecord.status), options.status),
        details: readDescriptorValue(errorRecord.details, options.details),
        originalName: readDescriptorValue(
            readOptionalString(errorRecord.originalName),
            options.originalName ?? error.name,
        ),
        cause: error,
    });
};

const toAppErrorFromRecord = (error: Record<string, unknown>, options: NormalizeUnknownErrorOptions = {}): AppError => {
    return createAppError({
        code: readOptionalString(error.code) ?? options.defaultCode ?? DEFAULT_APP_ERROR_CODE,
        summary: resolveFallbackSummary(error.message, options.defaultSummary),
        category: readOptionalString(error.category) ?? resolveFallbackCategory(error, options.defaultCategory),
        severity: readOptionalSeverity(error.severity) ?? options.severity ?? 'error',
        hint: readDescriptorValue(readOptionalString(error.hint), options.hint),
        retryable: readDescriptorValue(readOptionalBoolean(error.retryable), options.retryable),
        requestId: readDescriptorValue(readOptionalString(error.requestId), options.requestId),
        correlationId: readDescriptorValue(readOptionalString(error.correlationId), options.correlationId),
        operationId: readDescriptorValue(readOptionalString(error.operationId), options.operationId),
        capability: readDescriptorValue(readOptionalString(error.capability), options.capability),
        layer: readDescriptorValue(readOptionalString(error.layer), options.layer),
        operation: readDescriptorValue(readOptionalString(error.operation), options.operation),
        boundaryDisposition: readDescriptorValue(
            readOptionalBoundaryDisposition(error.boundaryDisposition),
            options.boundaryDisposition,
        ),
        clientId: readDescriptorValue(readOptionalString(error.clientId), options.clientId),
        platform: readDescriptorValue(readOptionalString(error.platform), options.platform),
        route: readDescriptorValue(readOptionalString(error.route), options.route),
        jobId: readDescriptorValue(readOptionalString(error.jobId), options.jobId),
        fingerprint: readDescriptorValue(readOptionalString(error.fingerprint), options.fingerprint),
        recommendedAction: readDescriptorValue(readOptionalString(error.recommendedAction), options.recommendedAction),
        status: readDescriptorValue(readOptionalNumber(error.status), options.status),
        details: readDescriptorValue(error.details, options.details),
        originalName: readDescriptorValue(readOptionalString(error.originalName ?? error.name), options.originalName),
        cause: toCause(error.cause),
    });
};

export const normalizeUnknownError = (error: unknown, options: NormalizeUnknownErrorOptions = {}): AppError => {
    if (isAppError(error)) {
        return error;
    }

    if (error instanceof ApiError) {
        return toAppErrorFromApiError(error, options);
    }

    if (error instanceof Error) {
        return toAppErrorFromError(error, options);
    }

    if (isObjectRecord(error)) {
        return toAppErrorFromRecord(error, options);
    }

    return createAppError({
        code: options.defaultCode ?? DEFAULT_APP_ERROR_CODE,
        summary: resolveFallbackSummary(error, options.defaultSummary),
        category: resolveFallbackCategory(error, options.defaultCategory),
        severity: options.severity ?? 'error',
        hint: options.hint,
        retryable: options.retryable ?? null,
        requestId: options.requestId,
        correlationId: options.correlationId,
        operationId: options.operationId,
        capability: options.capability,
        layer: options.layer,
        operation: options.operation,
        boundaryDisposition: options.boundaryDisposition,
        clientId: options.clientId,
        platform: options.platform,
        route: options.route,
        jobId: options.jobId,
        fingerprint: options.fingerprint,
        recommendedAction: options.recommendedAction,
        status: options.status,
        details: options.details,
        originalName: options.originalName ?? resolveFallbackOriginalName(error),
    });
};

export const wrapInAppError = (error: unknown, descriptor: AppErrorDescriptor): AppError => {
    const normalizedCause = normalizeUnknownError(error, {
        defaultCode: descriptor.code,
        defaultSummary: descriptor.summary,
        defaultCategory: descriptor.category,
        severity: descriptor.severity,
        retryable: descriptor.retryable,
        requestId: descriptor.requestId,
        correlationId: descriptor.correlationId,
        operationId: descriptor.operationId,
        capability: descriptor.capability,
        layer: descriptor.layer,
        operation: descriptor.operation,
        boundaryDisposition: descriptor.boundaryDisposition,
        clientId: descriptor.clientId,
        platform: descriptor.platform,
        route: descriptor.route,
        jobId: descriptor.jobId,
        fingerprint: descriptor.fingerprint,
        recommendedAction: descriptor.recommendedAction,
        status: descriptor.status,
        details: descriptor.details,
        originalName: descriptor.originalName,
        hint: descriptor.hint,
    });

    return createAppError({
        ...descriptor,
        severity: descriptor.severity ?? normalizedCause.severity,
        hint: readDescriptorValue(descriptor.hint, normalizedCause.hint),
        retryable: readDescriptorValue(descriptor.retryable, normalizedCause.retryable),
        requestId: readDescriptorValue(descriptor.requestId, normalizedCause.requestId),
        correlationId: readDescriptorValue(descriptor.correlationId, normalizedCause.correlationId),
        operationId: readDescriptorValue(descriptor.operationId, normalizedCause.operationId),
        capability: readDescriptorValue(descriptor.capability, normalizedCause.capability),
        layer: readDescriptorValue(descriptor.layer, normalizedCause.layer),
        operation: readDescriptorValue(descriptor.operation, normalizedCause.operation),
        boundaryDisposition: readDescriptorValue(descriptor.boundaryDisposition, normalizedCause.boundaryDisposition),
        clientId: readDescriptorValue(descriptor.clientId, normalizedCause.clientId),
        platform: readDescriptorValue(descriptor.platform, normalizedCause.platform),
        route: readDescriptorValue(descriptor.route, normalizedCause.route),
        jobId: readDescriptorValue(descriptor.jobId, normalizedCause.jobId),
        fingerprint: readDescriptorValue(descriptor.fingerprint, normalizedCause.fingerprint),
        recommendedAction: readDescriptorValue(descriptor.recommendedAction, normalizedCause.recommendedAction),
        status: readDescriptorValue(descriptor.status, normalizedCause.status),
        details: readDescriptorValue(descriptor.details, normalizedCause.details),
        originalName: readDescriptorValue(descriptor.originalName, normalizedCause.originalName),
        cause: normalizedCause,
    });
};

export const toAppErrorMetadata = (error: AppError): Record<string, unknown> => ({
    errorCode: error.code,
    errorCategory: error.category,
    errorSeverity: error.severity,
    errorSummary: error.summary,
    errorHint: error.hint,
    errorRetryable: error.retryable,
    requestId: error.requestId,
    correlationId: error.correlationId,
    operationId: error.operationId,
    capability: error.capability,
    layer: error.layer,
    operation: error.operation,
    boundaryDisposition: error.boundaryDisposition,
    clientId: error.clientId,
    platform: error.platform,
    route: error.route,
    jobId: error.jobId,
    fingerprint: error.fingerprint,
    recommendedAction: error.recommendedAction,
    status: error.status,
    errorOriginalName: error.originalName,
});
